import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Wifi, ShieldAlert, Cpu, Lock, Check, CheckCircle2 } from 'lucide-react';

export const SovereignNetworkDashboard: React.FC = () => {
    const [aptToken, setAptToken] = useState('');
    const [authStatus, setAuthStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [authMessage, setAuthMessage] = useState('');
    const [aiName, setAiName] = useState('');
    const [ingestData, setIngestData] = useState('');

    const handleHandshake = async () => {
        setAuthStatus('loading');
        try {
            const response = await fetch('/api/vortex/node/evaluate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${aptToken}`
                },
                body: JSON.stringify({ nodeId: 'LOCAL_NODE_001' })
            });

            if (response.ok) {
                setAuthStatus('success');
                setAuthMessage("Resonance synchronized. Sovereign Handshake Established.");
            } else {
                setAuthStatus('error');
                setAuthMessage("Resonance Mismatch. Handshake Failed.");
            }
        } catch (e) {
            setAuthStatus('error');
            setAuthMessage("Network Interface Unavailable.");
        }
    };

    const registerAI = async () => {
        if (authStatus !== 'success') return;
        try {
            const response = await fetch('/api/azl/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${aptToken}` },
                body: JSON.stringify({ name: aiName })
            });
            const data = await response.json();
            console.log("AI Registered:", data);
        } catch (e) { console.error("Registration failed", e); }
    };

    const runIngest = async () => {
        if (authStatus !== 'success') return;
        try {
            const response = await fetch('/api/azl/ingest', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${aptToken}` },
                body: JSON.stringify({ data: ingestData })
            });
            const data = await response.json();
            console.log("Data Ingested:", data);
        } catch (e) { console.error("Ingestion failed", e); }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 md:gap-8">
            <div className="space-y-6">
                <div className="p-4 md:p-6 rounded-2xl bg-white/5 border border-white/5 space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-lattice-cyan/10 rounded-xl text-lattice-cyan">
                            <Wifi className="w-5 h-5 md:w-6 md:h-6" />
                        </div>
                        <div>
                            <h4 className="text-sm font-display font-bold text-white uppercase">Sovereign Mesh Interlink</h4>
                            <p className="text-[9px] font-mono text-white/40 uppercase">Status: {authStatus === 'success' ? 'ACTIVE / SECURED' : 'WAITING FOR HANDSHAKE'}</p>
                        </div>
                    </div>
                </div>

                {/* Handshake Interface */}
                <div className="p-4 md:p-6 rounded-2xl bg-black/60 border border-lattice-cyan/25 space-y-4">
                    <div className="flex items-center gap-2 mb-2 text-lattice-cyan">
                        <Lock className="w-4 h-4" />
                        <span className="text-[9px] font-mono font-bold uppercase tracking-widest">Sovereign Handshake Engine</span>
                    </div>
                    <input
                        type="text"
                        placeholder="Enter Absolute Presence Token (APT_...)"
                        value={aptToken}
                        onChange={(e) => setAptToken(e.target.value)}
                        className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-xs md:text-sm font-mono text-white focus:outline-none focus:border-lattice-cyan/50"
                    />
                    <button
                        onClick={handleHandshake}
                        disabled={authStatus === 'loading'}
                        className="w-full py-3 bg-lattice-cyan/15 hover:bg-lattice-cyan hover:text-black border border-lattice-cyan/30 text-lattice-cyan font-mono text-[9px] md:text-[10px] font-bold uppercase rounded-xl tracking-wider transition-all"
                    >
                        {authStatus === 'loading' ? "ESTABLISHING RESONANCE..." : "INITIATE SOVEREIGN HANDSHAKE"}
                    </button>
                    {authMessage && (
                        <div className={`p-3 rounded-lg border text-[9px] font-mono flex items-center gap-2 ${authStatus === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-500'}`}>
                            {authStatus === 'success' ? <CheckCircle2 className="w-3 h-3" /> : <ShieldAlert className="w-3 h-3" />}
                            {authMessage}
                        </div>
                    )}
                </div>
            </div>

            <div className="space-y-6">
                <div className="p-4 md:p-6 rounded-2xl bg-white/5 border border-white/5 space-y-4">
                    <h4 className="text-sm font-display font-bold text-white uppercase">Sovereign Node Interface(s)</h4>
                    <input type="text" placeholder="AI Name" value={aiName} onChange={(e) => setAiName(e.target.value)} className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-xs md:text-sm font-mono text-white focus:outline-none" />
                    <button onClick={registerAI} disabled={authStatus !== 'success'} className="w-full py-2 bg-lattice-cyan/10 border border-lattice-cyan/20 text-lattice-cyan rounded-xl text-[9px] md:text-[10px] font-mono uppercase tracking-widest hover:bg-lattice-cyan/20">Register AI Node</button>
                    
                    <textarea placeholder="Ingest Data..." value={ingestData} onChange={(e) => setIngestData(e.target.value)} className="w-full bg-black/60 border border-white/10 rounded-xl px-4 py-3 text-xs md:text-sm font-mono text-white focus:outline-none h-32" />
                    <button onClick={runIngest} disabled={authStatus !== 'success'} className="w-full py-2 bg-lattice-cyan/10 border border-lattice-cyan/20 text-lattice-cyan rounded-xl text-[9px] md:text-[10px] font-mono uppercase tracking-widest hover:bg-lattice-cyan/20">Ingest Data</button>
                </div>
            </div>
        </div>
    );
};
